import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../common/ui/dialog';
import { Button } from '../../common/ui/button';
import { Input } from '../../common/ui/input';
import { Label } from '../../common/ui/label';
import { Textarea } from '../../common/ui/textarea';
import { AGENT_TEMPLATES, BLANK_TEMPLATE, getTemplateById } from '../../config/agentTemplates';
import { buildQuickAgentPayload } from '../../utils/agentDefaultProviders';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';

const STEPS = ['template', 'basics', 'review'];

export default function AgentSimpleWizard({ isOpen, onClose, onSubmit, onOpenAdvanced }) {
  const [step, setStep] = useState(0);
  const [templateId, setTemplateId] = useState('customer-support');
  const [name, setName] = useState('');
  const [greeting, setGreeting] = useState('');
  const [persona, setPersona] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStep(0);
    setTemplateId('customer-support');
    setName('');
    setGreeting('');
    setPersona('');
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = getTemplateById(id);
    setGreeting(t.greeting || '');
    setPersona(t.persona || '');
    if (!name.trim() && id !== 'blank') {
      setName(t.name);
    }
  };

  const currentTemplate = getTemplateById(templateId);
  const stepKey = STEPS[step];

  const handleCreate = async () => {
    if (!name.trim() || !persona.trim()) return;
    setSubmitting(true);
    try {
      const payload = buildQuickAgentPayload({
        name: name.trim(),
        greeting: greeting.trim() || currentTemplate.greeting,
        persona: persona.trim(),
        useCase: currentTemplate.useCase || 'marketing',
      });
      await onSubmit(payload);
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-500" />
            Quick agent setup
          </DialogTitle>
          <DialogDescription>
            Step {step + 1} of {STEPS.length} — defaults handle voice & AI providers. Customize later in
            advanced settings.
          </DialogDescription>
        </DialogHeader>

        {stepKey === 'template' && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Pick a starting template</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {[...AGENT_TEMPLATES, BLANK_TEMPLATE].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => applyTemplate(t.id)}
                  className={`text-left p-3 rounded-lg border-2 transition-colors ${
                    templateId === t.id
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-border hover:border-indigo-300'
                  }`}
                >
                  <span className="text-lg mr-2">{t.icon}</span>
                  <span className="font-medium text-sm">{t.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {stepKey === 'basics' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="wizard-name">Agent name *</Label>
              <Input
                id="wizard-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Support Line"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wizard-greeting">Opening greeting</Label>
              <Input
                id="wizard-greeting"
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
                placeholder={currentTemplate.greeting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wizard-persona">Instructions *</Label>
              <Textarea
                id="wizard-persona"
                rows={4}
                value={persona}
                onChange={(e) => setPersona(e.target.value)}
                placeholder="How should this agent behave on calls?"
              />
            </div>
          </div>
        )}

        {stepKey === 'review' && (
          <div className="space-y-3 text-sm rounded-lg border p-4 bg-muted/40">
            <p>
              <span className="text-muted-foreground">Name:</span> {name}
            </p>
            <p>
              <span className="text-muted-foreground">Template:</span> {currentTemplate.name}
            </p>
            <p className="line-clamp-3">
              <span className="text-muted-foreground">Instructions:</span> {persona}
            </p>
            <p className="text-xs text-muted-foreground">
              STT, LLM, and voice use recommended defaults. Open advanced editor after creation to
              change providers.
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenAdvanced?.()}>
            Advanced editor instead
          </Button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button type="button" variant="outline" onClick={() => setStep((s) => s - 1)}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button
                type="button"
                onClick={() => {
                  if (stepKey === 'template') applyTemplate(templateId);
                  setStep((s) => s + 1);
                }}
                disabled={stepKey === 'basics' && (!name.trim() || !persona.trim())}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleCreate}
                disabled={submitting || !name.trim() || !persona.trim()}
              >
                {submitting ? 'Creating…' : 'Create agent'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
